import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type React from 'react';

interface EmptyStateProps {
  /** Icon to display */
  icon: LucideIcon;
  /** Main title/heading */
  title?: string;
  /** Description message */
  message: string;
  /** Optional secondary message */
  secondaryMessage?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Generic empty state component for displaying when no content is available.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={SearchX}
 *   title="No Results Found"
 *   message="Try adjusting your search filters."
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  message,
  secondaryMessage,
  className,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: {
      container: 'py-4 px-3',
      icon: 'w-8 h-8 mb-2',
      title: 'text-sm font-medium mb-1',
      message: 'text-xs',
      secondary: 'text-xs mt-1',
    },
    md: {
      container: 'py-6 sm:py-8 px-3 sm:px-4',
      icon: 'w-10 h-10 sm:w-12 sm:h-12 mb-2 sm:mb-3',
      title: 'text-base sm:text-lg font-semibold mb-1 sm:mb-2',
      message: 'text-sm sm:text-base',
      secondary: 'text-xs sm:text-sm mt-1 sm:mt-2',
    },
    lg: {
      container: 'py-10 px-6',
      icon: 'w-12 h-12 mb-4',
      title: 'text-lg font-semibold mb-2',
      message: 'text-sm',
      secondary: 'text-sm mt-2',
    },
  };

  const sizes = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center h-full w-full',
        sizes.container,
        className
      )}
    >
      <Icon className={cn('text-content-disabled', sizes.icon)} />
      {title && (
        <p className={cn('text-content-secondary', sizes.title)}>{title}</p>
      )}
      <p className={cn('text-content-muted', sizes.message)}>{message}</p>
      {secondaryMessage && (
        <p className={cn('text-content-muted max-w-md', sizes.secondary)}>
          {secondaryMessage}
        </p>
      )}
    </div>
  );
};
